import {formatNumber} from "@progress/kendo-intl";
import {IWidgetData} from "../types/IWidgetData";
import {IBoardObjectNode} from "../types/IBoardObjectNode";
import {IBoardMeasure} from "../types/IBoardMeasure";
import { IGridData } from '../components/widget/chart/types/IGridData';
import { IChartSetup } from '../components/widget/chart/types/IChartSetup';
import { ITimePeriod } from '../types/ITimePeriod';

const useCircleChart = () => {
    const _registerGridRecord = (value: IWidgetData, periodHash: any, amount: number, gridData: IGridData[], measures: IBoardMeasure[]) => {
        let gridValue = periodHash[value['periodId']];

        let measure = measures.find((measure) => {
            return measure.id === value['measureId'];
        });

        if (!gridValue) {
            gridValue = {
                periodId: value['periodId'],
                periodName: value['periodName'],
                endDate: value['endDate']
            };
            periodHash[value['periodId']] = gridValue;
            gridData.push(gridValue);
        }

        if (measure) {
            gridValue['_' + value['nodeId'] + '_' + value['measureId']] = formatNumber(amount, `${(measure.isPercent ? 'p' : 'n') + measure.scale}`);
        } else {
            gridValue['_' + value['nodeId'] + '_' + value['measureId']] = amount;
        }
    };

    const _updatePercentages = (widgetData: IWidgetData[], total: number, setup: IChartSetup) => {
        for (let i = 0; i < widgetData.length; i++) {
            let data = widgetData[i];
            data['percentage'] = data['amount'] / total;
            if (setup.percentages)
                data['value'] = data['percentage'];
        }
    };

    const generateCircleData = (nodes: IBoardObjectNode[], widgetData: IWidgetData[], setup: IChartSetup, measures: IBoardMeasure[], selectedPeriod?: ITimePeriod,) => {
        let data: any[] = [];
        let gridData: IGridData[] = [];
        let periodHash: any = {};

        if (!widgetData) return widgetData;

        let series = setup.series && setup.series[0];
        let total = 0;

        for (let i = 0; i < widgetData.length; i++) {
            let value = widgetData[i];
            let node = nodes.find((n) => n.id === value['nodeId']);

            if (value['numericValue']) {
                data.push({
                    category: value['objectName'],
                    value: value['numericValue'],
                    amount: value['numericValue'],
                    lookupRef: {measureId: value['templatePropertyId'], scale: value['scale']},
                });
            }

            const selectedPeriodId = selectedPeriod && selectedPeriod.id;

            if (value['periodId'] === selectedPeriodId && value['measureId'].toString() === series?.measureId?.toString() && node) {
                let amount = value['amount'] / (series?.amountsIn || 1);

                data.push({
                    category: node.name,
                    value: amount,
                    amount: amount,
                    percentage: setup.percentages,
                    lookupRef: {
                        nodeId: node.id,
                        measureId: value['measureId'],
                        scale: setup.series && setup.series[0].scale
                    },
                });

                total += value['amount'];

                _registerGridRecord(value, periodHash, amount, gridData, measures);
            }
        }

        _updatePercentages(widgetData, total, setup);

        const serie = [
            {
                type: setup.type,
                data: data,
                labels: {
                    visible: setup.seriesLabels,
                    background: 'transparent',
                    position: 'outsideEnd',
                    callbackTemplate: (e) => {
                        return (
                            e.category +
                            ': ' +
                            formatNumber(
                                e.dataItem.percentage ? e.percentage : e.value,
                                e.dataItem.percentage ? `p${e.dataItem.lookupRef.scale}` : `n${e.dataItem.lookupRef.scale}`,
                            )
                        );
                    },
                },
                tooltip: {
                    visible: !setup.seriesLabels,
                    callbackTemplate: (e) => {
                        if (e.point) {
                            return (
                                e.point.dataItem &&
                                e.point.category + ': ' + formatNumber(e.point.value, e.point.dataItem.percentage ? `p` : `n${e.point.dataItem.lookupRef.scale}`)
                            );
                        }
                    },
                },
                legend: {
                    visible: setup.legend !== 'none',
                    position: setup.legend,
                },
            },
        ];

        return {serie, gridData};
    };

    return {
        generateCircleData
    }
}

export default useCircleChart;